import { notFound } from "@tanstack/react-router";

/**
 * Draft routes — host-gated, not flag-gated.
 *
 * Every route listed in DRAFT_ROUTES still renders placeholder content. They stay in the
 * codebase as working templates and remain fully viewable on the Lovable
 * preview host, but they must never be reachable on the live domain.
 *
 * The gate is the request host, deliberately: an environment variable that
 * someone forgets to set is exactly how draft pages end up public. A host
 * check has no "forgot to configure" state — production is production.
 *
 * These routes also carry `noindex` and are excluded from sitemap.xml, and no
 * link anywhere in the site points at them.
 */
export const PRODUCTION_HOSTS = ["www.laaconsulting.com", "laaconsulting.com"] as const;

export const DRAFT_ROUTES = [
  "/about",
  "/how-we-work",
  "/pricing",
  "/services",
  "/services/sba-loan-consulting",
  "/services/business-plan-writing",
  "/services/grants-and-funding",
  "/tools",
  "/guides/sba-loan-guide",
  "/guides/business-plan-checklist",
  "/lp/sba-loans",
  "/lp/business-plan",
  "/lp/$campaign",
] as const;

export function isProductionHost(host: string | undefined | null): boolean {
  if (!host) return false;
  const bare = host.split(":")[0]!.toLowerCase();
  return (PRODUCTION_HOSTS as readonly string[]).includes(bare);
}

/**
 * Call from a draft route's `beforeLoad`. On the production host this throws
 * `notFound()`, which renders the standard not-found component with a genuine
 * HTTP 404 on the server response. On any other host it is a no-op.
 */
export async function assertDraftRouteVisible(): Promise<void> {
  let host: string | undefined;
  if (import.meta.env.SSR) {
    const { getDraftRequestHost } = await import("./draft-routes.server");
    host = getDraftRequestHost();
  } else if (typeof window !== "undefined") {
    host = window.location.hostname;
  }
  if (isProductionHost(host)) {
    throw notFound();
  }
}
